<template>
  <div v-show="dataLoaded">
    <div class="l-gap--md">
      <div class="p-archive-list u-position--relative">
        <div class="p-archive-slider p-archive-slider--portrait swiper">
          <div class="swiper-wrapper">
            <div
              v-for="(post, index) in firstSixPosts"
              :key="index"
              class="swiper-slide"
            >
              <article class="p-archive-item-portrait">
                <a
                  :href="post.url"
                  class="p-archive-item-portrait__inner"
                  target="_blank"
                >
                  <div class="p-archive-item-portrait__main">
                    <div class="p-archive-item-portrait__thumb c-media-thumb c-media-thumb--portrait">
                      <img
                        v-if="post.vertical_image"
                        :src="post.vertical_image"
                        alt=""
                        width="340"
                        height="595"
                      >
                      <img
                        v-else
                        :src="post.image"
                        alt=""
                        width="340"
                        height="595"
                      >
                    </div>
                    <header class="p-archive-item-portrait__header">
                      <h3 class="p-archive-item-portrait__title c-title-obi c-title-obi--small">
                        <span>{{ post.title }}</span>
                      </h3>
                      <div class="p-archive-item-portrait__categories">
                        <span
                          class="p-archive-item-portrait__category c-title-obi c-title-obi--category"
                        ><span>{{ post.category }}</span></span>
                      </div>
                    </header>
                    <time
                      class="p-archive-item-portrait__date"
                      :datetime="post.createdAt"
                    ><span
                      class="year"
                    >{{ new Date(post.createdAt).getFullYear() }}</span><span class="date">{{ formatDate(post.createdAt) }}</span></time>
                  </div>
                </a>
                <ul class="c-list-text-tags">
                  <li
                    v-for="tag in post.tags"
                    :key="tag.id"
                  >
                    <a
                      :href="'/media/blog_tag/'+ tag.slug +'/'"
                      target="_blank"
                    >{{ tag.name }}</a>
                  </li>
                </ul>
              </article>
            </div>
          </div>
        </div>
        <div class="p-nav-swiper-container u-visible--sm">
          <button
            type="button"
            class="p-nav-swiper-around-nav-block p-nav-swiper-around-nav-block__prev swiper-button-prev"
            aria-label="前へ"
          >
            <span class="c-icon">
              <img
                src="/nbl/cms/wp-content/themes/isi-nbl/assets/img/shared/icon_arrow_circle.svg"
                alt=""
                width="60"
                height="60"
                loading="lazy"
              >
            </span>
          </button>
          <button
            type="button"
            class="p-nav-swiper-around-nav-block p-nav-swiper-around-nav-block__next swiper-button-next"
            aria-label="次へ"
          >
            <span class="c-icon">
              <img
                src="/nbl/cms/wp-content/themes/isi-nbl/assets/img/shared/icon_arrow_circle.svg"
                alt=""
                width="60"
                height="60"
                loading="lazy"
              >
            </span>
          </button>
        </div>
      </div>
    </div>
    <div class="l-gap--lg l-container">
      <div
        class="l-grid-row-3--sm u-grid-gap--sm js-fix-height-box"
        data-each-num-md="3"
        data-each-num-sm="3"
      >
        <article
          v-for="(post, index) in remainingPosts"
          :key="index + 6"
          class="p-archive-item-blog"
        >
          <div class="p-archive-item-blog__inner">
            <div class="p-archive-item-blog__thumb">
              <img
                :src="post.image"
                alt=""
                width="360"
                height="240"
                loading="lazy"
              >
              <time
                class="p-archive-item-blog__date"
                :datetime="post.createdAt"
              ><span class="year">{{ new Date(post.createdAt).getFullYear() }}</span><span
                class="date"
              >{{ formatDate(post.createdAt) }}</span></time>
            </div>
            <div class="p-archive-item-blog__body">
              <a
                :href="post.url"
                class="p-archive-item-blog__title"
                target="_blank"
              >
                <div
                  class="p-archive-item-blog__title__inner js-fix-height-box__item"
                  data-height-group="1"
                >
                  <h3 class="p-archive-item-blog__title__text">
                    {{ post.title }}</h3>
                  <span
                    class="p-archive-item-blog__category c-title-obi c-title-obi--category"
                  ><span>{{ post.category }}</span></span>
                </div>
              </a>
              <ul class="c-list-text-tags">
                <li
                  v-for="tag in post.tags"
                  :key="tag.id"
                >
                  <a :href="'/media/blog_tag/'+ tag.slug +'/'">{{ tag.name }}</a>
                </li>
              </ul>
            </div>
          </div>
        </article>
      </div>
      <div class="l-gap--md-2 u-align--center">
        <a
          href="/media/blog_cat/nbl/"
          target="_blank"
          class="c-button js-change-hovered"
        >
          <span class="text">もっと見る</span>
          <span
            class="c-icon right"
            aria-hidden="true"
          >
            <span class="c-icon__inner" />
          </span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';

import ArchiveSlider from './ArchiveSlider';


// API
const posts = ref({});
const dataLoaded = ref(false);

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  }
});

// 最初の6つの投稿を取得する計算プロパティ
const firstSixPosts = computed(() => {
  // posts.value が配列であり、かつ1つ以上の要素がある場合にのみスライスを実行
  if (Array.isArray(posts.value) && posts.value.length > 0) {
    return posts.value.slice(0, 6);
  } else {
    // posts.value が空の配列の場合や配列でない場合は、空の配列を返す
    return [];
  }
});

// 7つ目以降の投稿を取得する計算プロパティ
const remainingPosts = computed(() => {
  if (Array.isArray(posts.value) && posts.value.length > 0) {
  return posts.value.slice(6);
  } else {
    return [];
  }
});

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // 月を2桁にする
  const day = date.getDate().toString().padStart(2, '0'); // 日を2桁にする
  return `${month}${day}`;
};

const currentUrl = window.location.pathname;
const urlParts = currentUrl.split('/');

const school = urlParts.find(segment => segment !== '');

const params = {
  taxonomy: 'blog_cat',
  category: school,
};

const apiGet = async () => {
  const response = await api.get('/media/api/', { params });
  posts.value = response.data.posts;
};

onMounted(async () => {
  // コンポーネントがマウントされたときにapiGetメソッドを呼び出します
  await apiGet();
  dataLoaded.value = true;
  const ArchiveSliderElem = document.getElementsByClassName('p-archive-slider');
  if (!ArchiveSliderElem.length) return;
  for (let i = 0; i < ArchiveSliderElem.length; i++) {
    new ArchiveSlider(ArchiveSliderElem[i], i);
  }
});

</script>
